import z from "zod";

// User group schemas
const createUserGroupSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
  externalId: z.string().optional(),
});

const updateUserGroupSchema = z.object({
  name: z.string().min(1, "Name is required").optional(),
  description: z.string().min(1, "Description is required").optional(),
  externalId: z.string().optional(),
});

const findUserGroupsFiltersSchema = z.object({
  page: z.coerce.number().default(1),
  per_page: z.coerce.number().default(10),
  filter: z
    .object({
      search: z.string().optional(),
    })
    .optional(),
});

const userGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  externalId: z.string().nullable().optional(),
  isDefault: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const findUserGroupsSchema = z.object({
  data: z.array(userGroupSchema),
  count: z.number(),
});

const findUserGroupsInfiniteInputSchema = z.object({
  limit: z.coerce.number().default(20),
  cursor: z.number().optional(),
  filter: z
    .object({
      search: z.string().optional(),
    })
    .optional(),
});

const findUserGroupsInfiniteSchema = z.object({
  data: z.array(userGroupSchema),
  nextCursor: z.number().optional(),
  count: z.number(),
});

// Agent connection assignment schemas
const assignAgentConnectionsSchema = z.object({
  userGroupId: z.string().uuid(),
  agentConnectionIds: z.array(z.string().uuid()),
});

const removeAgentConnectionsSchema = z.object({
  userGroupId: z.string().uuid(),
  agentConnectionIds: z.array(z.string().uuid()),
});

const userGroupWithConnectionsSchema = userGroupSchema.extend({
  agentConnections: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    type: z.string(),
    enabled: z.boolean(),
    createdAt: z.coerce.date(),
  })),
});

// User assignment schemas
const assignUsersSchema = z.object({
  userGroupId: z.string().uuid(),
  userIds: z.array(z.string()),
});

const removeUsersSchema = z.object({
  userGroupId: z.string().uuid(),
  userIds: z.array(z.string()),
});

const userGroupWithUsersSchema = userGroupSchema.extend({
  users: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().email().optional(),
    createdAt: z.coerce.date(),
  })),
});

// IDP Group Import schemas
const importGroupsSchema = z.object({
  groupIds: z.array(z.string()).min(1, "At least one group must be selected"),
  assignUsers: z.boolean().default(true),
});

const idpGroupSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  mail: z.string().optional(),
  mailEnabled: z.boolean(),
  securityEnabled: z.boolean(),
  groupTypes: z.array(z.string()),
  createdDateTime: z.string(),
  source: z.string().optional(),
});

const importPreviewSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  description: z.string(),
  memberCount: z.number(),
  usersWillBeFound: z.number(),
  usersNotInSystem: z.number(),
  alreadyExists: z.boolean(),
  action: z.enum(['create', 'update']),
});

const importResultSchema = z.object({
  groupId: z.string(),
  groupName: z.string(),
  success: z.boolean(),
  error: z.string().optional(),
  usersAssigned: z.number(),
  usersNotFound: z.number(),
});

const importResponseSchema = z.object({
  totalGroups: z.number(),
  successfulImports: z.number(),
  failedImports: z.number(),
  results: z.array(importResultSchema),
});

export const createUserGroup = createUserGroupSchema;
export const updateUserGroup = updateUserGroupSchema;
export const findUserGroups = findUserGroupsSchema;
export const findUserGroup = userGroupSchema;
export const assignAgentConnections = assignAgentConnectionsSchema;
export const removeAgentConnections = removeAgentConnectionsSchema;
export const userGroupWithConnections = userGroupWithConnectionsSchema;
export const assignUsers = assignUsersSchema;
export const removeUsers = removeUsersSchema;
export const userGroupWithUsers = userGroupWithUsersSchema;

export const userGroupValidators = {
  createUserGroupSchema,
  updateUserGroupSchema,
  findUserGroupsFiltersSchema,
  userGroupSchema,
  findUserGroupsSchema,
  findUserGroupsInfiniteInputSchema,
  findUserGroupsInfiniteSchema,
  assignAgentConnectionsSchema,
  removeAgentConnectionsSchema,
  userGroupWithConnectionsSchema,
  assignUsersSchema,
  removeUsersSchema,
  userGroupWithUsersSchema,
  // IDP Import schemas
  importGroupsSchema,
  idpGroupSchema,
  importPreviewSchema,
  importResultSchema,
  importResponseSchema,
};
